/**
 * "DER GESCHMACKS-KOMPASS" — Signature 2.
 * One code path for mouse, touch and pen via Pointer Events; arrow keys
 * move the marker in 5% steps for keyboard users. Reordering uses CSS
 * `order` on the results list (a flex/grid container) instead of manually
 * animating DOM positions — the browser's own layout handles the actual
 * movement, and there's nothing here that can get a transform/position
 * calculation subtly wrong under fast dragging.
 *
 * Distance is computed on every pointer move but the DOM reorder itself
 * only commits on pointerup (or on each keydown, which is already
 * discrete) — reordering live while dragging read as jumpy in testing,
 * and nothing in the design calls for it to reorder mid-drag.
 *
 * Drag trail: a fixed pool of dot elements (built once, never added to or
 * removed from) is repositioned from a short position history on every
 * animation frame while dragging, each dot's size/blur/opacity fixed by
 * its slot so only left/top change per frame -- cheaper than creating and
 * destroying DOM nodes as the gesture moves. Skipped entirely under
 * prefers-reduced-motion, same as the rest of this file's motion.
 */
(function () {
  function initCompass(section) {
    var pad = section.querySelector('[data-compass-pad]');
    var marker = section.querySelector('[data-compass-marker]');
    var cards = Array.prototype.slice.call(section.querySelectorAll('[data-compass-card]'));
    var announce = section.querySelector('[data-compass-announce]');
    if (!pad || !marker || !cards.length) return;

    var x = 50;
    var y = 50;
    var dragging = false;

    // First-use nudge (see candy-taste-compass.css): the marker alone
    // wasn't discoverable as draggable in live testing, so a hint bubble
    // and pulsing ring invite interaction until the visitor actually
    // uses it once -- remembered in localStorage so it never comes back
    // for someone who already knows.
    var INTERACTED_KEY = 'bbCompassInteracted';
    try {
      if (window.localStorage && localStorage.getItem(INTERACTED_KEY) === '1') {
        pad.classList.add('has-interacted');
      }
    } catch (e) {}
    function markInteracted() {
      if (pad.classList.contains('has-interacted')) return;
      pad.classList.add('has-interacted');
      try {
        if (window.localStorage) localStorage.setItem(INTERACTED_KEY, '1');
      } catch (e) {}
    }

    // Drag trail (see candy-taste-compass.css .candy-compass-trail*).
    var reduceMotion = window.matchMedia &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    var trailEl = section.querySelector('[data-compass-trail]');
    var TRAIL_LENGTH = 12;
    var trailDots = [];
    var trailOpacities = [];
    var trailHistory = [];
    var trailRaf = null;
    if (trailEl && !reduceMotion) {
      for (var t = 0; t < TRAIL_LENGTH; t++) {
        var frac = t / (TRAIL_LENGTH - 1);
        var dot = document.createElement('span');
        dot.className = 'candy-compass-trail__dot';
        var size = 14 - 11 * frac; // 14px (freshest) down to 3px (oldest)
        dot.style.width = size + 'px';
        dot.style.height = size + 'px';
        dot.style.filter = 'blur(' + (0.4 + 2.2 * frac).toFixed(2) + 'px)';
        trailEl.appendChild(dot);
        trailDots.push(dot);
        trailOpacities.push((0.55 * (1 - frac)).toFixed(3));
      }
    }

    function renderTrail() {
      trailRaf = null;
      for (var i = 0; i < trailDots.length && i < trailHistory.length; i++) {
        var p = trailHistory[i];
        trailDots[i].style.left = p.x + '%';
        trailDots[i].style.top = (100 - p.y) + '%';
        trailDots[i].style.opacity = trailOpacities[i];
      }
    }

    function pushTrail() {
      if (!trailDots.length) return;
      trailHistory.unshift({ x: x, y: y });
      if (trailHistory.length > TRAIL_LENGTH) trailHistory.length = TRAIL_LENGTH;
      if (trailRaf === null) trailRaf = requestAnimationFrame(renderTrail);
    }

    function clearTrail() {
      if (!trailDots.length) return;
      trailHistory = [];
      if (trailRaf !== null) {
        cancelAnimationFrame(trailRaf);
        trailRaf = null;
      }
      trailDots.forEach(function (dot) {
        dot.style.opacity = '0';
      });
    }

    function clamp(v) {
      return Math.max(0, Math.min(100, v));
    }

    function closestCardName() {
      var best = null;
      var bestDist = Infinity;
      cards.forEach(function (card) {
        var cx = parseFloat(card.dataset.x);
        var cy = parseFloat(card.dataset.y);
        var dist = Math.hypot(cx - x, cy - y);
        if (dist < bestDist) {
          bestDist = dist;
          best = card;
        }
      });
      return best;
    }

    function reorder() {
      var withDist = cards.map(function (card) {
        var cx = parseFloat(card.dataset.x);
        var cy = parseFloat(card.dataset.y);
        return { card: card, dist: Math.hypot(cx - x, cy - y) };
      });
      withDist.sort(function (a, b) {
        return a.dist - b.dist;
      });
      withDist.forEach(function (entry, index) {
        entry.card.style.order = index;
        entry.card.classList.toggle('is-closest', index === 0);
      });
      var closest = withDist[0] && withDist[0].card;
      var title = closest && closest.querySelector('.candy-compass-card__title');
      if (announce && title) {
        announce.textContent = title.textContent + ' passt am besten.';
      }
    }

    function setPosition(newX, newY, commit) {
      x = clamp(newX);
      y = clamp(newY);
      marker.style.left = x + '%';
      marker.style.top = (100 - y) + '%'; // y=100 (scharf) is visually the TOP of the pad
      if (commit) reorder();
    }

    function positionFromEvent(event) {
      var rect = pad.getBoundingClientRect();
      var px = ((event.clientX - rect.left) / rect.width) * 100;
      var py = 100 - ((event.clientY - rect.top) / rect.height) * 100;
      return { x: px, y: py };
    }

    marker.addEventListener('pointerdown', function (event) {
      dragging = true;
      marker.setPointerCapture(event.pointerId);
      pad.classList.add('is-dragging');
      markInteracted();
    });

    marker.addEventListener('pointermove', function (event) {
      if (!dragging) return;
      var pos = positionFromEvent(event);
      setPosition(pos.x, pos.y, false);
      pushTrail();
    });

    function endDrag(event) {
      if (!dragging) return;
      dragging = false;
      pad.classList.remove('is-dragging');
      var pos = positionFromEvent(event);
      setPosition(pos.x, pos.y, true);
      clearTrail();
    }
    marker.addEventListener('pointerup', endDrag);
    marker.addEventListener('pointercancel', endDrag);

    // Tapping/clicking anywhere on the pad itself (not just the marker)
    // also moves the marker there directly — a smaller, equally valid
    // interaction alongside the drag.
    pad.addEventListener('pointerdown', function (event) {
      if (event.target === marker) return;
      var pos = positionFromEvent(event);
      setPosition(pos.x, pos.y, true);
      markInteracted();
    });

    marker.addEventListener('keydown', function (event) {
      var step = 5;
      var handled = true;
      switch (event.key) {
        case 'ArrowRight':
          setPosition(x + step, y, true);
          break;
        case 'ArrowLeft':
          setPosition(x - step, y, true);
          break;
        case 'ArrowUp':
          setPosition(x, y + step, true);
          break;
        case 'ArrowDown':
          setPosition(x, y - step, true);
          break;
        case 'Home':
          setPosition(50, 50, true);
          break;
        default:
          handled = false;
      }
      if (handled) {
        event.preventDefault();
        markInteracted();
      }
    });

    // Initial state: no card is "closest" until the visitor actually
    // interacts, so the grid starts in its natural (Theme Editor) order
    // rather than pre-judging a default taste.
  }

  function init() {
    document.querySelectorAll('.candy-compass-section').forEach(initCompass);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
